import { ParsedUrlQuery } from 'querystring';

import { GetStaticPaths, GetStaticProps } from 'next';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';

import { Layout } from '@/components/Layout';
import { Button } from '@/components/shared/Button';
import { Icons, IconsTypes } from '@/components/shared/Icons';
import api from '@/helpers/api';
import { sortSiteMenu } from '@/helpers/common';
import { getI18nPaths, getI18nProps } from '@/helpers/i18n';
import { COMMON_LOCALE_PART } from '@/locale';
import { IPageWithMenuProps, Locale, SiteMenuItem, ISEOOptions, Colors } from '@/types';

export default function NotFoundPage({ siteMenu }: IPageWithMenuProps) {
  const { t } = useTranslation();

  const router = useRouter();

  const seoOptions: ISEOOptions = {
    title: t('seo-text.title'),
    description: '',
  };

  return (
    <Layout siteMenu={siteMenu} seoOptions={seoOptions}>
      <section className="container flex min-h-screen flex-col items-center justify-center">
        <div className="pb-11 md:pt-28">
          <Icons name={IconsTypes.NOT_FOUND} />
        </div>
        <h2 className="mb-6 text-3xl md:text-4xl">{t('not-found.title')}</h2>
        <p className="text-center text-md">{t('not-found.text')}</p>
        <Button className="mt-11" type="button" variant={Colors.primary} onClick={() => router.push('/')}>
          {t('not-found.button')}
        </Button>
      </section>
    </Layout>
  );
}

interface IParams extends ParsedUrlQuery {
  type: string;
  locale: Locale;
}

interface NotFoundPageProps extends GetStaticProps {
  params: IParams;
}

export const getStaticPaths: GetStaticPaths = () => {
  return {
    fallback: false,
    paths: getI18nPaths(),
  };
};

export const getStaticProps = async ({ params }: NotFoundPageProps) => {
  const { locale } = params;

  const request = api({ locale });

  const [{ data: siteMenu }] = await Promise.all([request<SiteMenuItem[]>('site-menu/')]);

  return {
    props: {
      ...(await getI18nProps(locale, [COMMON_LOCALE_PART])),
      siteMenu: sortSiteMenu(locale, siteMenu),
    },
  };
};
